import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';
import { useReviewSession } from '@/features/review/useReviewSession';
import { TranscriptTimeline } from '@/components/review/TranscriptTimeline';
import { reviewTranscriptBlocksToTimeline } from '@/types/review';

function SeverityBadge({ severity }: { severity: string }) {
  const c: Record<string, { label: string; cls: string }> = {
    critical: { label: '重大', cls: 'bg-red-100 text-red-800' },
    high: { label: '高', cls: 'bg-orange-100 text-orange-800' },
    medium: { label: '中', cls: 'bg-yellow-100 text-yellow-800' },
    low: { label: '低', cls: 'bg-blue-100 text-blue-800' },
  };
  const cfg = c[severity] ?? { label: severity, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${cfg.cls}`}>{cfg.label}</span>;
}

interface Finding {
  severity: string;
  title: string;
  body: string;
  section_index: number;
}
interface Comment {
  id: string;
  reviewer_name: string;
  comment_kind: string;
  body: string;
  created_at: string;
}

export function FeedbackPage() {
  const { sessionId } = useParams();
  const user = useAuthStore((s) => s.currentUser);
  const [session, setSession] = useState<Record<string, unknown> | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);

  // 文字起こし: /sessions/{id}/review からの transcript_blocks を表示する。
  // /feedback エンドポイントは ai_result / review_comments のみで transcript を含まないため、
  // review エンドポイントを併用する（C 系で実装済みの useReviewSession を再利用）。
  const review = useReviewSession(sessionId);
  const timelineBlocks = useMemo(() => {
    if (!review.detail) return [];
    return reviewTranscriptBlocksToTimeline(
      review.detail.transcript_blocks,
      review.detail.session_feedback_items,
    );
  }, [review.detail]);
  const [selectedUtteranceId, setSelectedUtteranceId] = useState<string | undefined>();

  useEffect(() => {
    Promise.all([
      api.get(`/sessions/${sessionId}`),
      api.get(`/sessions/${sessionId}/feedback`),
    ])
      .then(([sessResp, fbResp]) => {
        setSession(sessResp.data);
        setFindings(fbResp.data.ai_result?.findings ?? []);
        setComments(fbResp.data.review_comments ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [sessionId]);

  if (loading) return <div className="p-8 text-center text-gray-400">読み込み中...</div>;
  if (!session) return <div className="p-8 text-center text-red-500">セッションが見つかりません</div>;

  const isCoachAckPending = session.status === 'coach_ack_pending';
  const isCompleted = session.status === 'completed';
  const isAllSkipped = session.outcome === 'all_skipped';
  const isCoach = user && String(session.coach_user_id) === String(user.id);

  return (
    <div>
      <div className="mb-4">
        <Link to="/sessions" className="text-sm text-blue-600 hover:underline">
          ← セッション一覧
        </Link>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">フィードバック閲覧</h1>
          <p className="mt-1 text-sm text-gray-500">
            {session.client_name as string} / {session.coach_name as string} —{' '}
            {session.day_type as string} #{session.serial_no as number}
          </p>
        </div>
        {isCoachAckPending && isCoach && (
          <button
            type="button"
            className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            見ましたマーク
          </button>
        )}
        {isCompleted && (
          <span className="rounded bg-green-100 px-3 py-1.5 text-sm text-green-700">確認済み</span>
        )}
      </div>

      {isAllSkipped && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-600">今回は人手レビューなし（レビュー対象外）</p>
        </div>
      )}

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="col-span-1 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">AI 指摘（{findings.length}件）</h3>
          {findings.map((f, i) => (
            <div key={i} className="rounded-lg bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <SeverityBadge severity={f.severity} />
                <span className="text-xs text-gray-400">セクション {f.section_index}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-gray-900">{f.title}</p>
              <p className="mt-1 text-sm text-gray-600">{f.body}</p>
            </div>
          ))}
          {findings.length === 0 && <p className="text-sm text-gray-400">AI 指摘はありません</p>}
        </div>

        <div className="col-span-1">
          <h3 className="text-sm font-semibold text-gray-700">文字起こし</h3>
          <div className="mt-3 flex max-h-[600px] flex-col overflow-hidden rounded-lg bg-white shadow-sm">
            {review.loading && (
              <p className="p-4 text-sm text-gray-400">文字起こしを読み込み中...</p>
            )}
            {!review.loading && review.error && (
              <p className="p-4 text-sm text-gray-500">
                文字起こしを取得できませんでした: {review.error}
              </p>
            )}
            {!review.loading && !review.error && timelineBlocks.length === 0 && (
              <p className="p-4 text-sm text-gray-400">文字起こしデータがありません</p>
            )}
            {!review.loading && !review.error && timelineBlocks.length > 0 && (
              <TranscriptTimeline
                blocks={timelineBlocks}
                viewMode="full"
                selectedUtteranceId={selectedUtteranceId}
                onUtteranceSelect={(u) => setSelectedUtteranceId(u.id)}
              />
            )}
          </div>
        </div>

        <div className="col-span-1 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">レビュアーコメント</h3>
          {!isAllSkipped &&
            comments.map((c) => (
              <div key={c.id} className="rounded-lg bg-white p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{c.reviewer_name}</span>
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${c.comment_kind === 'primary' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {c.comment_kind === 'primary' ? 'レビュー' : '追記'}
                  </span>
                </div>
                <p className="mt-2 text-sm text-gray-700">{c.body}</p>
                <p className="mt-1 text-xs text-gray-400">
                  {new Date(c.created_at).toLocaleString('ja-JP', {
                    month: 'numeric',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            ))}
          {isAllSkipped && <p className="text-sm text-gray-400">レビューコメントはありません</p>}
          {!isAllSkipped && comments.length === 0 && (
            <p className="text-sm text-gray-400">まだコメントはありません</p>
          )}
        </div>
      </div>
    </div>
  );
}
